import _ from 'lodash';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { SetRequired } from 'type-fest';
import styles from './TextWithIcon.module.scss';

const ICON_OFFSET_CORRECTION = 1;

interface WrapGroupProps {
  text: React.ReactNode;
  icon: React.ReactNode;
  noWrap?: boolean;
}
const WrapGroup = React.forwardRef<HTMLSpanElement, WrapGroupProps>(
  ({ text, icon, noWrap }, ref) => {
    let leadingComponent = text;
    let trailingComponent = icon;

    if (typeof text === 'string') {
      const chars = _.split(text, '');
      const rest = chars.slice(0, chars.length - 1).join('');
      const last = chars[chars.length - 1];
      leadingComponent = rest;
      trailingComponent = (
        <span className={styles.wrappingGroup}>
          {last}
          {icon}
        </span>
      );
    }

    return (
      <span ref={ref} className={noWrap ? styles.wrappingGroup : styles.wrap}>
        {leadingComponent}
        {trailingComponent}
      </span>
    );
  },
);

const ELLIPSIS = '…';

interface IconAwareTruncateTextProps {
  icon: React.ReactNode;
  text: string;
  maxLines: number;
}

const IconAwareTruncateText = ({
  icon,
  text,
  maxLines,
}: IconAwareTruncateTextProps) => {
  const [renderedText, setRenderedText] = useState(text);
  const containerRef = useRef<HTMLSpanElement>(null);
  const iconRef = useRef<HTMLSpanElement>(null);
  const textContainerRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const containerEl = containerRef.current;
    const textEl = textContainerRef.current;
    if (!textEl || !containerEl) return undefined;

    const lineHeight = parseInt(getComputedStyle(textEl).lineHeight, 10);
    const maxContainerHeight = lineHeight * maxLines;

    const truncate = (currentText: string, currentIteration: number) => {
      // Since we know that the text can't be truncated more than its length,
      // we can set this as an upper bound for preventing dancing icons
      if (currentIteration > text.length) return;

      // We only want to manipulate the text node of the WrapGroup, not the last letter wrapped with the icon
      const textNode =
        textEl.childNodes.length > 1 ? textEl.childNodes[0] : null;
      if (!(textNode instanceof Text)) return;

      if (currentText === text) {
        // We do this because in the wrapping group, we take the last character
        // and group it with the icon, so we need to remove it here to not have it twice
        textNode.textContent = currentText.slice(0, currentText.length - 1);
      } else {
        // we don't append the ellipsis here since if there is one, it's grouped with the icon
        textNode.textContent = currentText.trimEnd();
      }

      if (
        containerEl.clientHeight - ICON_OFFSET_CORRECTION >
          maxContainerHeight ||
        textEl.getBoundingClientRect().width >
          containerEl.getBoundingClientRect().width
      ) {
        const nextText = currentText.slice(0, currentText.length - 1);
        truncate(nextText, currentIteration + 1);
      } else {
        // At this point we are done and we can also update the react state accordingly
        setRenderedText(
          currentText === text ? currentText : currentText.trimEnd() + ELLIPSIS,
        );
      }
    };

    const observer = new ResizeObserver(() => {
      truncate(text, 0);
    });

    observer.observe(containerEl);

    return () => {
      observer.disconnect();
    };
  }, [text, maxLines]);

  return (
    <span ref={containerRef}>
      <WrapGroup
        text={renderedText}
        icon={
          <span className={styles.iconContainer} ref={iconRef}>
            {icon}
          </span>
        }
        ref={textContainerRef}
      />
    </span>
  );
};

interface TruncateContentProps {
  icon?: React.ReactNode;
  text: string;
}

const MultiLineTruncateContent = ({ icon, text }: TruncateContentProps) => {
  if (!icon) {
    return <span className={styles.multiLine}>{text}</span>;
  }

  return <IconAwareTruncateText icon={icon} text={text} maxLines={2} />;
};

const SingeLineTruncateContent = ({ icon, text }: TruncateContentProps) => {
  return (
    <>
      <span className={styles.singleLine}>{text}</span>
      {icon}
    </>
  );
};

type TextWithIconProps = {
  icon?: React.ReactNode;
  noWrap?: boolean;
  to?: string;
} & (
  | {
      truncate: 'single' | 'multi';
      text: string;
    }
  | {
      truncate?: never;
      text: React.ReactNode;
    }
);

const TruncatedTextWithIcon = ({
  text,
  icon,
  truncate,
  to,
}: SetRequired<Omit<TextWithIconProps, 'noWrap'>, 'truncate'> & {
  text: string;
}) => {
  const props: TruncateContentProps = {
    icon,
    text,
  };

  const content =
    truncate === 'single' ? (
      <SingeLineTruncateContent {...props} />
    ) : (
      <MultiLineTruncateContent {...props} />
    );

  return (
    <div className={styles.truncate}>
      {to ? <Link to={to}>{content}</Link> : content}
    </div>
  );
};

const TextWithIcon = (props: TextWithIconProps) => {
  if (props.truncate) {
    return <TruncatedTextWithIcon {...props} truncate={props.truncate} />;
  }

  return (
    <WrapGroup text={props.text} icon={props.icon} noWrap={props.noWrap} />
  );
};

export default TextWithIcon;
